package com.arena_web.core.viewmodel

import com.arena_web.data.dto.*
import com.arena_web.domain.repositories.ClubAdminRepository
import com.arena_web.domain.repositories.ClubPublicRepository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

data class ClubDetailUiState(
    val isLoading: Boolean = true,
    val club: ClubDto? = null,
    val gallery: List<ClubGalleryImageDto> = emptyList(),
    val rules: List<ClubRuleDto> = emptyList(),
    val fields: List<FieldDto> = emptyList(),
    val amenities: List<String> = emptyList(),
    val posts: List<ClubPostDto> = emptyList(),
    val isLoadingPosts: Boolean = false,
    val hasMorePosts: Boolean = true,
    val currentPostsPage: Int = 1,
    val reviews: List<ClubReviewDto> = emptyList(),
    val isLoadingReviews: Boolean = false,
    val hasMoreReviews: Boolean = true,
    val currentReviewsPage: Int = 1,
    val showReviewForm: Boolean = false,
    val isSubmittingReview: Boolean = false,
    val error: String? = null
)

class ClubDetailViewModel(
    private val clubId: String,
    private val clubPublicRepository: ClubPublicRepository,
    private val clubAdminRepository: ClubAdminRepository? = null
) : BaseViewModel() {

    private val _uiState = MutableStateFlow(ClubDetailUiState())
    val uiState: StateFlow<ClubDetailUiState> = _uiState.asStateFlow()

    override fun onCreate() {
        super.onCreate()
        loadClubDetail()
        loadPosts()
        loadReviews()
    }

    private fun loadClubDetail() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }

            clubPublicRepository.getClubDetail(clubId).onSuccess { detail ->
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        club = detail.club,
                        gallery = detail.gallery,
                        rules = detail.rules,
                        fields = detail.fields,
                        amenities = detail.amenities
                    )
                }
            }.onFailure { e ->
                _uiState.update { it.copy(isLoading = false, error = e.message) }
            }
        }
    }

    private suspend fun fetchFeed(params: Map<String, String>): Result<ClubPostPageDto> {
        return clubAdminRepository?.getFeedPaged(clubId, params)
            ?: clubPublicRepository.getFeed(clubId, params)
    }

    private fun loadPosts() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoadingPosts = true) }

            val params = mapOf("page" to "1", "size" to "10")
            fetchFeed(params).onSuccess { page ->
                _uiState.update {
                    it.copy(
                        posts = page.items,
                        isLoadingPosts = false,
                        currentPostsPage = 1,
                        hasMorePosts = page.items.size < page.totalItems
                    )
                }
            }.onFailure {
                _uiState.update { it.copy(isLoadingPosts = false) }
            }
        }
    }

    fun toggleLike(postId: String) {
        val repo = clubAdminRepository ?: return
        val current = _uiState.value
        val post = current.posts.find { it.id == postId } ?: return

        // Optimistic update
        val updatedPosts = current.posts.map {
            if (it.id == postId) it.copy(
                isLikedByMe = !it.isLikedByMe,
                likesCount = if (it.isLikedByMe) it.likesCount - 1 else it.likesCount + 1
            ) else it
        }
        _uiState.update { it.copy(posts = updatedPosts) }

        viewModelScope.launch {
            repo.likePost(clubId, postId).onFailure {
                // Revert on failure
                val revertedPosts = _uiState.value.posts.map {
                    if (it.id == postId) it.copy(
                        isLikedByMe = post.isLikedByMe,
                        likesCount = post.likesCount
                    ) else it
                }
                _uiState.update { it.copy(posts = revertedPosts) }
            }
        }
    }

    fun loadMorePosts() {
        val current = _uiState.value
        if (current.isLoadingPosts || !current.hasMorePosts) return

        viewModelScope.launch {
            _uiState.update { it.copy(isLoadingPosts = true) }

            val nextPage = current.currentPostsPage + 1
            val params = mapOf("page" to "$nextPage", "size" to "10")

            fetchFeed(params).onSuccess { page ->
                _uiState.update {
                    it.copy(
                        posts = it.posts + page.items,
                        isLoadingPosts = false,
                        currentPostsPage = nextPage,
                        hasMorePosts = page.items.isNotEmpty() && (it.posts.size + page.items.size) < page.totalItems
                    )
                }
            }.onFailure {
                _uiState.update { it.copy(isLoadingPosts = false) }
            }
        }
    }

    // ── Reviews ──

    private fun loadReviews() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoadingReviews = true) }

            val params = mapOf("page" to "1", "size" to "20")
            clubPublicRepository.getReviews(clubId, params).onSuccess { page ->
                _uiState.update {
                    it.copy(
                        reviews = page.items,
                        isLoadingReviews = false,
                        currentReviewsPage = 1,
                        hasMoreReviews = page.items.size < page.totalItems
                    )
                }
            }.onFailure {
                _uiState.update { it.copy(isLoadingReviews = false) }
            }
        }
    }

    fun loadMoreReviews() {
        val current = _uiState.value
        if (current.isLoadingReviews || !current.hasMoreReviews) return

        viewModelScope.launch {
            _uiState.update { it.copy(isLoadingReviews = true) }

            val nextPage = current.currentReviewsPage + 1
            val params = mapOf("page" to "$nextPage", "size" to "20")

            clubPublicRepository.getReviews(clubId, params).onSuccess { page ->
                _uiState.update {
                    it.copy(
                        reviews = it.reviews + page.items,
                        isLoadingReviews = false,
                        currentReviewsPage = nextPage,
                        hasMoreReviews = page.items.isNotEmpty() && (it.reviews.size + page.items.size) < page.totalItems
                    )
                }
            }.onFailure {
                _uiState.update { it.copy(isLoadingReviews = false) }
            }
        }
    }

    fun showReviewForm() {
        _uiState.update { it.copy(showReviewForm = true) }
    }

    fun hideReviewForm() {
        _uiState.update { it.copy(showReviewForm = false) }
    }

    fun submitReview(rating: Int, comment: String) {
        val repo = clubAdminRepository ?: return
        if (rating < 1 || rating > 5) return

        viewModelScope.launch {
            _uiState.update { it.copy(isSubmittingReview = true) }

            val request = CreateClubReviewRequest(
                rating = rating.toString(),
                comment = comment.ifBlank { null }
            )

            repo.createReview(clubId, request).onSuccess {
                _uiState.update { it.copy(isSubmittingReview = false, showReviewForm = false) }
                loadReviews()
                loadClubDetail()
            }.onFailure {
                _uiState.update { it.copy(isSubmittingReview = false) }
            }
        }
    }
}
